import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import GenericPieChart from '../../../../genericPieChart/GenericPieChart';
import * as css from './SubscriptionSummary.css';
import {SubscriptionPlan} from '../../../../../../../redux/subscription/subscription.type';
    
interface SubscriptionSummaryProps {
    totalSold: number;
    totalAmount: number;
    totalSeries: number;
    totalEvents: number;
    activeEventsCount: number;
    plans: SubscriptionPlan[];
}

const GRAY_COLOR =  '#CCCCCC'; //getPrimitive('grays.gray300');

function SubscriptionSummary({
    totalSold,
    totalAmount,
    totalSeries,
    totalEvents,
    activeEventsCount,
    plans
}: SubscriptionSummaryProps) {
    const {t} = useTranslation('realtime');

    const totalPercentage = totalAmount > 0 ? Math.round((totalSold / totalAmount) * 100) : 0;

    // Transform series data for pie chart - colored segments for sold, one gray segment for total unsold
    const chartData = useMemo(() => {
        if(!plans.length || totalAmount === 0) {
            return [{value: 1, color: GRAY_COLOR}];
        }
        const data: {value: number; color: string}[] = [];

        // Add colored segments for each series' sold portion
        plans.forEach(plan => {
            if(plan.amount - plan.amountAvailable > 0) {
                data.push({
                    value: plan.amount - plan.amountAvailable,
                    color: plan.color
                });
            }
        });

        // Add one gray segment for total unsold
        const totalUnsold = totalAmount - totalSold;
        if(totalUnsold > 0) {
            data.push({
                value: totalUnsold,
                color: GRAY_COLOR
            });
        }

        return data.length > 0 ? data : [{value: 1, color: GRAY_COLOR}];
    }, [plans, totalAmount, totalSold]);

    return (
        <css.summarySection>
            <css.chartWrapper>
                <GenericPieChart
                    data={chartData}
                    dataKey="value"
                    halfCircle={true}
                    radius={{innerRadius: 48, outerRadius: 58}}
                    paddingAngle={2}
                    cornerRadius={4}
                    inlineRect={
                        <>
                            <css.percentage>{totalPercentage}%</css.percentage>
                            <css.percentageLabel>{t('soldLabel')}</css.percentageLabel>
                        </>
                    }
                />
            </css.chartWrapper>
            <css.statsContainer>
                <css.statItem>{totalSeries} {t('totalSeriesLabel')}</css.statItem>
                <css.statItem>{totalEvents} {t('totalEventsLabel')}</css.statItem>
                <css.activeEventsBar className='free-color'>
                    <css.activeEventsCount>{activeEventsCount || 0}</css.activeEventsCount>
                    <css.activeEventsText>{t('activeEventsLabel')}</css.activeEventsText>
                </css.activeEventsBar>
            </css.statsContainer>
        </css.summarySection> 
    );
}

export default SubscriptionSummary;
